/**
 * ColumnAdapter - Maps legacy react-table columns to TanStack Table format
 * Maintains 100% API compatibility with existing column definitions
 */
/* eslint-disable arrow-body-style */
import React from "react";
import { ColumnDef } from "@tanstack/react-table";
import { rankItem } from "@tanstack/match-sorter-utils";
import styles from "../Table.module.scss";

// Re-export legacy Filter type for compatibility
export interface LegacyFilter {
  id: string;
  value: string;
}

// Dropdown filter component factory
export const createDropdownFilter = (options: (string | number)[]) => {
  const DropdownFilter = ({ column }: { column: any }) => {
    const allOptions = options.length > 1 ? ["Show All", ...options] : options;
    return (
      <select
        value={column.getFilterValue() || "Show All"}
        onChange={(e) => column.setFilterValue(e.target.value === "Show All" ? undefined : e.target.value)}
        className={styles.dropdown}
      >
        {allOptions.map((option, index) => (
          <option key={index} value={option}>
            {option}
          </option>
        ))}
      </select>
    );
  };
  DropdownFilter.displayName = "DropdownFilter";
  return DropdownFilter;
};

export class ColumnAdapter {
  /**
   * Maps a legacy State.Column to TanStack ColumnDef
   */
  static mapColumn(column: any): ColumnDef<any> {
    // Handle the case where the accessor returns JSX (like Binary components)
    const accessor = column.ownAccessor || column.accessor;
    
    let accessorFn: (row: any) => any;
    let cellRenderer: any = null;
    let isAccessorJsxRenderer = false;
    
    if (typeof accessor === "function") {
      // Check if this accessor returns JSX by testing with a sample call
      try {
        // Create a dummy object to test the accessor
        const testObj = { mask: "FFFFFFFF", name: "test", enumeration: null };
        const testResult = accessor(testObj);
        
        // If the accessor returns JSX (has $$typeof property), separate data access from rendering
        if (testResult && typeof testResult === "object" && testResult.$$typeof) {
          // This is a JSX element, so we need to extract the underlying data
          // For Binary components, we need to extract the actual mask value for sorting/filtering
          accessorFn = (row: any) => row.mask || "";
          
          // The cell renderer uses the original accessor function
          cellRenderer = accessor;
          isAccessorJsxRenderer = true;
        } else {
          // Normal function accessor
          accessorFn = accessor;
        }
      } catch (error) {
        // Fallback to using the accessor as-is
        accessorFn = accessor;
      }
    } else if (typeof accessor === "string") {
      accessorFn = (row: any) => row[accessor];
    } else {
      accessorFn = (row: any) => row[column.id || column.name];
    }

    // Create base column definition
    const baseColumn: ColumnDef<any> = {
      id: column.id || column.name,
      accessorFn,
      header: this.mapHeader(column),
      size: column.width,
      minSize: column.minWidth || 100,
      maxSize: column.maxWidth,
      enableSorting: column.sortable !== false,
      enableColumnFilter: column.filterable !== false,
    };

    // Handle custom cell rendering (either from Cell property or JSX-returning accessor)
    if (cellRenderer || (column.Cell && typeof column.Cell === "function")) {
      const renderFunction = cellRenderer || column.Cell;
      
      baseColumn.cell = ({ row }) => {
        try {
          if (isAccessorJsxRenderer) {
            // Accessor JSX renderers are legacy row accessors and must receive the raw row.
            return (renderFunction as any)(row.original);
          }

          // Build a flattened row object matching legacy react-table's cellInfo.row shape
          // In old react-table, cellInfo.row had column names as keys with accessor results as values
          const flatRow: any = {};
          for (const cell of row.getAllCells()) {
            flatRow[cell.column.id] = cell.getValue();
          }
          // Pass legacy react-table CellInfo shape so Cell renderers using props.value work correctly
          const value = accessorFn(row.original);
          const result = (renderFunction as any)({ value, row: flatRow, original: row.original, column });
          return result;
        } catch (error) {
          console.warn("Cell rendering error:", error);
          // Fallback: return the raw value
          return String(accessorFn(row.original) || "");
        }
      };
    }

    // Handle dropdown filtering
    if (column.hasDropdownFiltering && column.filterDropdownOptions) {
      baseColumn.filterFn = (row, columnId, filterValue) => {
        if (!filterValue || filterValue === "all") return true;
        const cellValue = row.getValue(columnId);
        // For multi-value columns (comma-separated), check if any individual value matches
        if ((column as any).isMultiValueDropdown && typeof cellValue === "string") {
          return cellValue.split(",").some((val: string) => val.trim() === filterValue);
        }
        return cellValue === filterValue;
      };
      
      baseColumn.meta = {
        ...baseColumn.meta,
        filterComponent: createDropdownFilter(column.filterDropdownOptions),
      };
    }

    // Handle custom sort method
    if (column.sortMethod) {
      baseColumn.sortingFn = (rowA, rowB, columnId) => {
        const a = rowA.getValue(columnId);
        const b = rowB.getValue(columnId);
        return (column.sortMethod as any)(a, b);
      };
    }

    // Handle column props/styling
    if (column.getProps) {
      const props = column.getProps();
      const style = (props as any)?.style;
      
      // Filter out justify-content from styles as rt-th elements are flex items
      const filteredStyle = style ? Object.fromEntries(
        Object.entries(style).filter(([key]) => key !== "justify-content")
      ) : undefined;
      
      baseColumn.meta = {
        ...baseColumn.meta,
        style: filteredStyle,
        className: column.headerClassName,
        isCheckboxColumn: column.isCheckboxColumn,
      };
    }

    return baseColumn;
  }

  /**
   * Maps legacy header definition to TanStack format
   */
  private static mapHeader(column: any): any {
    if (typeof column.Header === "function") {
      // Handle function headers (like hidden headers)
      const headerFunc = column.Header;
      if (headerFunc.toString().includes("return null") || headerFunc === (() => null)) {
        return () => null;
      }
      return () => column.name;
    }
    
    // Handle string/other headers
    return column.Header || column.name;
  }

  /**
   * Maps multiple columns at once
   */
  static mapColumns(columns: any[]): ColumnDef<any>[] {
    return columns
      .filter(column => column.isVisible !== false) // More permissive filtering
      .map(column => this.mapColumn(column));
  }

  /**
   * Creates fuzzy filter function for global search
   */
  static createFuzzyFilter() {
    return (row: any, columnId: string, value: string, addMeta: any) => {
      const itemRank = rankItem(row.getValue(columnId), value);
      addMeta({ itemRank });
      return itemRank.passed;
    };
  }

  /**
   * Creates default filter function that matches legacy behavior
   */
  static createDefaultFilter() {
    return (row: any, columnId: string, filterValue: string) => {
      const value = row.getValue(columnId);
      if (!filterValue) return true;
      if (value === null || value === undefined) return false;
      
      // Case-insensitive contains match (matches original react-table behavior)
      const cellStr = String(value).toLowerCase();
      const filterStr = String(filterValue).toLowerCase();
      return cellStr.includes(filterStr);
    };
  }

  /**
   * Legacy search match rule implementation
   */
  private static searchMatchRule(cellValue: string, filterValue: string): boolean {
    if (!cellValue || !filterValue) return true;
    
    // Convert to lowercase for case-insensitive matching
    const cell = cellValue.toLowerCase();
    const filter = filterValue.toLowerCase();
    
    // Simple contains match (matches original behavior)
    return cell.includes(filter);
  }
}